<script>
export default {
  name: 'CandidateFilterMenuItem',
  props: {
    title: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    icon: {
      type: String,
      default: '',
    },
    actionClass: {
      type: [Array, Object, String],
      default: '',
    },
    actionPosition: {
      type: String,
      default: 'append',
      validator: (value) => ['append', 'prepend'].includes(value),
    },
    count: {
      type: Number,
      default: 0,
    },
    selected: {
      type: Boolean,
      default: false,
    },
    tabindex: {
      type: Number,
      default: 0,
    },
    type: {
      type: String,
      default: 'nav',
      validator: (value) => ['nav', 'switch', 'sort'].includes(value),
    },
    switchValue: {
      type: Boolean,
      default: false,
    },
    sortDir: {
      type: String,
      default: 'desc',
      validator: (value) => ['asc', 'desc'].includes(value),
    },
  },
  computed: {
    hasPrependAction() {
      return this.actionPosition === 'prepend'
    },
    hasAppendAction() {
      return this.actionPosition === 'append'
    },
  },
  methods: {
    focus() {
      this.$el.focus()
    },
  },
}
</script>

<template>
  <v-list-item
    :tabindex="tabindex"
    :disabled="disabled"
    :class="{ 'list-item-error': error }"
    @click="$emit('click')"
  >
    <v-list-item-action v-if="hasPrependAction" :class="actionClass">
      <slot name="action">
        <v-checkbox
          :input-value="selected"
          hide-details
          class="ma-0 pa-0"
          @change="$emit('change', $event)"
          @click.stop
        />
      </slot>
    </v-list-item-action>

    <v-list-item-icon v-if="icon" class="mr-3 filter-item-icon">
      <transition name="fade-cycle-down" mode="out-in">
        <v-icon v-if="error" key="error" color="red lighten-1" class="mt-2"
          >mdi-alert-circle-outline</v-icon
        >
        <v-icon v-else key="icon">{{ icon }}</v-icon>
      </transition>
    </v-list-item-icon>

    <v-list-item-content>
      <v-list-item-title
        :class="[
          { 'pt-2': error },
          { 'filter-menu-item-title': !hasPrependAction },
        ]"
      >
        {{ title }}
        <div v-if="error" class="filter-item-error red--text text--lighten-1">
          {{ $t('candidateList.filters.error') }}
        </div>
      </v-list-item-title>
    </v-list-item-content>

    <v-list-item-action
      v-if="hasAppendAction"
      :class="[
        actionClass,
        { 'switch-action': type === 'switch' },
        { 'd-flex flex-row align-center mt-2 mb-5': type === 'nav' },
      ]"
    >
      <slot name="action">
        <template v-if="type === 'switch'">
          <v-switch :input-value="switchValue" hide-details class="ma-0 pa-0" />
        </template>
        <template v-else-if="type === 'sort'">
          <transition name="sort-dir-icon">
            <span v-if="selected" class="sort-dir-icon-wrapper">
              <v-icon
                size="20"
                color="primary"
                class="sort-dir-icon"
                :class="{ 'sort-dir-icon--desc': sortDir === 'desc' }"
                >mdi-arrow-up</v-icon
              >
            </span>
          </transition>
        </template>
        <template v-else>
          <span v-if="count > 0" class="mr-2 text-caption">
            {{ count }}
          </span>
          <v-icon v-if="!loading">mdi-chevron-right</v-icon>
          <v-progress-circular
            v-else
            size="16"
            width="2"
            class="my-1"
            indeterminate
          />
        </template>
      </slot>
    </v-list-item-action>
  </v-list-item>
</template>

<style lang="scss" scoped>
@import '@/styles/_vue-transitions.scss';

.filter-menu-item-title {
  padding-top: 10px !important;
}

.filter-item-error {
  margin-top: 2px;
  font-size: 10px;
  line-height: 1.2;
}

.switch-action {
  margin-top: 6px !important;
  margin-bottom: 18px !important;
}

.sort-dir-icon-wrapper {
  display: inline-flex;
  align-items: center;
}

.sort-dir-icon {
  transition: transform 0.25s ease;

  &--desc {
    transform: rotate(180deg);
  }
}

.sort-dir-icon-enter-active,
.sort-dir-icon-leave-active {
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.sort-dir-icon-enter,
.sort-dir-icon-leave-to {
  opacity: 0;
  transform: scale(0);
}
</style>
